/**
 * /clinics-like-yours — Public self-discovery landing (iter 90 + 90b)
 *
 * Public marketing page positioning Nodera as relevant to clinics like
 * the visitor's, then gating a deeper "fit briefing" tool behind a CTA
 * lead capture.
 *
 * Public content is intentionally qualitative — pain points → Nodera
 * answers — with NO math, revenue figures, or program-specific
 * recommendations on the public surface. The projection math now lives
 * behind /api/admin/clinics-like-yours/match (admin-only).
 *
 * Conversion flow:
 *   1. Visitor reads pain-point → answer cards.
 *   2. Visitor submits the CTA form (email + clinic profile filters).
 *   3. Page reveals a qualitative thank-you stating the right programs
 *      depend on the clinic's unique makeup and a tailored briefing
 *      will follow within 1 business day.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ArrowRight,
  CheckCircle2,
  Inbox,
  Layers,
  Loader2,
  Lock,
  MapPin,
  Receipt,
  ShieldCheck,
  Stethoscope,
  Users2,
  Workflow,
  Zap,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const PAGE_TITLE = "Clinics Like Yours Are Using Nodera Health";
const PAGE_DESC =
  "See how clinics like yours use Nodera Health to package programs, route vendor payments, and grow membership revenue. Request your tailored fit briefing.";

// ---------------------------------------------------------------------------
// Pain-point → Nodera answer cards (qualitative, no numbers, no program names)
// ---------------------------------------------------------------------------
const PAIN_POINTS = [
  {
    icon: Layers,
    pain: "Your team stitches together labs, supplements, peptides, and provider review by hand for every patient.",
    answer:
      "Nodera packages the whole patient journey — assessment, lab orders, provider review, fulfillment, follow-up — into turnkey programs your clinic can activate in one click. Standardized, branded to you, ready to sell.",
  },
  {
    icon: Receipt,
    pain: "Vendor payments are a tangle: pharmacies, DME, lab partners, and providers all want money on different terms — and refunds are a nightmare.",
    answer:
      "Built-in multi-party payments split the patient's checkout across your clinic, vendors, providers, and pharmacy partners automatically. Refunds reverse every leg in one click, with a full audit trail.",
  },
  {
    icon: Workflow,
    pain: "Stalled patients fall through the cracks. You don't know who's overdue for labs, who hasn't started fulfillment, who's about to churn.",
    answer:
      "Every program ships with an opinionated workflow tracker — kanban-style boards by status, automated SLA detection, exception center surfacing stalled enrollments and overdue invoices in one view.",
  },
  {
    icon: ShieldCheck,
    pain: "Compliance is a moving target — state licensure, DEA, BAA, professional services, consent. Hard to know if you're covered for every program in every state.",
    answer:
      "Per-service compliance flags, state-availability gating, automated documentation checks, and a profit-margin floor block any program from going live if it has a coverage gap. The publish gate is the operator's safety net.",
  },
  {
    icon: Zap,
    pain: "Recurring memberships are great in theory, but reserve accounting + renewal management is hard to operate.",
    answer:
      "Subscription-mode checkout, vendor-reserve mechanics for quarterly labs and annual reassessments, and renewal/churn dashboards built specifically for clinic-membership economics.",
  },
  {
    icon: Stethoscope,
    pain: "Every clinic is unique. Templates from generic platforms don't fit your panel mix, your state, or your specialty.",
    answer:
      "Programs are templates, not straitjackets. Tier inclusions, service routes, vendor partners, and pricing all tune to your clinic. Our team helps map your unique makeup to the right starting flagship.",
  },
];

// ---------------------------------------------------------------------------
// Components
// ---------------------------------------------------------------------------
function PainPointCard({ icon: Icon, pain, answer, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-2xl border border-slate-200 bg-white p-6 hover:shadow-md transition"
    >
      <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-slate-900 text-white mb-4">
        <Icon className="w-5 h-5" />
      </div>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-rose-600 mb-1">
        The pain
      </div>
      <p className="text-sm text-slate-700 leading-relaxed">{pain}</p>
      <div className="mt-4 pt-4 border-t border-slate-100">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-emerald-700 mb-1">
          How Nodera answers
        </div>
        <p className="text-sm text-slate-900 leading-relaxed">{answer}</p>
      </div>
    </div>
  );
}

function GatedToolForm({ vocab, onSubmitted }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [clinicName, setClinicName] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [state, setState] = useState("");
  const [size, setSize] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!email.trim()) {
      toast.error("Please enter your email to access your fit briefing.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/public/clinics-like-yours/lead", {
        email: email.trim(),
        name: name.trim() || null,
        clinic_name: clinicName.trim() || null,
        specialty: specialty || null,
        state: state || null,
        size: size || null,
        notes: notes.trim() || null,
      });
      onSubmitted?.({
        email: email.trim(),
        clinic_name: clinicName.trim() || null,
        specialty,
        state,
        size,
        next_step: r.data?.next_step,
      });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="cly-gated-form"
      className="rounded-3xl border border-slate-200 bg-white shadow-xl p-6 md:p-8"
    >
      <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-slate-900 text-white mx-auto mb-4">
        <Lock className="w-5 h-5" />
      </div>
      <div className="text-center mb-6">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">
          Access the fit briefing tool
        </div>
        <h2 className="mt-2 text-2xl sm:text-3xl font-bold text-slate-900">
          Which Nodera programs fit your clinic?
        </h2>
        <p className="mt-3 text-sm text-slate-600 max-w-lg mx-auto">
          Tell us a bit about your practice. Our team will analyze your
          clinic&apos;s unique makeup and follow up with a tailored briefing
          on the programs most relevant to you.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block md:col-span-2">
          <span className="text-[10px] uppercase font-semibold text-slate-500">Email *</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="cly-email"
            className="mt-1 w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-slate-900 focus:outline-none focus:border-slate-900"
            placeholder="you@yourclinic.com"
          />
        </label>

        <label className="block">
          <span className="text-[10px] uppercase font-semibold text-slate-500">Your name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="cly-name"
            className="mt-1 w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-slate-900 focus:outline-none focus:border-slate-900"
            placeholder="Dr. Jane Smith"
          />
        </label>

        <label className="block">
          <span className="text-[10px] uppercase font-semibold text-slate-500">Clinic name</span>
          <input
            type="text"
            value={clinicName}
            onChange={(e) => setClinicName(e.target.value)}
            data-testid="cly-clinic-name"
            className="mt-1 w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-slate-900 focus:outline-none focus:border-slate-900"
            placeholder="Acme Functional Medicine"
          />
        </label>

        <label className="block">
          <span className="text-[10px] uppercase font-semibold text-slate-500 flex items-center gap-1">
            <Stethoscope className="w-3 h-3" /> Specialty
          </span>
          <select
            value={specialty}
            onChange={(e) => setSpecialty(e.target.value)}
            data-testid="cly-specialty"
            className="mt-1 w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-slate-900 focus:outline-none focus:border-slate-900"
          >
            <option value="">Select…</option>
            {(vocab.specialties || []).map((s) => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="text-[10px] uppercase font-semibold text-slate-500 flex items-center gap-1">
            <Users2 className="w-3 h-3" /> Clinic size
          </span>
          <select
            value={size}
            onChange={(e) => setSize(e.target.value)}
            data-testid="cly-size"
            className="mt-1 w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-slate-900 focus:outline-none focus:border-slate-900"
          >
            <option value="">Select…</option>
            {(vocab.sizes || []).map((s) => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
        </label>

        <label className="block md:col-span-2">
          <span className="text-[10px] uppercase font-semibold text-slate-500 flex items-center gap-1">
            <MapPin className="w-3 h-3" /> Primary state
          </span>
          <select
            value={state}
            onChange={(e) => setState(e.target.value)}
            data-testid="cly-state"
            className="mt-1 w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-slate-900 focus:outline-none focus:border-slate-900"
          >
            <option value="">Any / Multiple</option>
            {(vocab.states || []).map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </label>

        <label className="block md:col-span-2">
          <span className="text-[10px] uppercase font-semibold text-slate-500">
            Anything we should know? (optional)
          </span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            data-testid="cly-notes"
            rows={2}
            className="mt-1 w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-slate-900 focus:outline-none focus:border-slate-900"
            placeholder="What programs are you most curious about?"
          />
        </label>
      </div>

      <button
        type="button"
        onClick={submit}
        disabled={submitting}
        data-testid="cly-submit"
        className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 text-white text-sm font-bold px-5 py-3 hover:bg-slate-800 disabled:opacity-50"
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
        {submitting ? "Submitting…" : "Get my tailored briefing"}
      </button>
      <p className="mt-3 text-[10px] text-slate-500 text-center">
        Single-use access. No spam. We respond within one business day.
      </p>
    </div>
  );
}

function ThankYouPanel({ result }) {
  return (
    <div
      data-testid="cly-success"
      className="rounded-3xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-8 md:p-10 shadow-xl"
    >
      <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-emerald-600 text-white mx-auto mb-5">
        <CheckCircle2 className="w-7 h-7" />
      </div>
      <div className="text-center">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-emerald-700">
          Request received
        </div>
        <h3 className="mt-2 text-2xl sm:text-3xl font-bold text-slate-900">
          Thanks{result?.email ? `, ${result.email.split("@")[0]}` : ""}.
        </h3>
        <p className="mt-4 text-sm text-slate-700 max-w-xl mx-auto leading-relaxed">
          Every clinic&apos;s right-fit programs depend on its unique makeup —
          specialty mix, panel size, state regulations, and vendor
          availability all matter. We don&apos;t make recommendations until our
          team has reviewed your inputs in context.
        </p>
        <p className="mt-3 text-sm text-slate-700 max-w-xl mx-auto leading-relaxed">
          You&apos;ll receive your tailored briefing within{" "}
          <span className="font-semibold">one business day</span> at{" "}
          <span className="font-mono text-slate-900">{result?.email}</span>.
        </p>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-xl mx-auto text-left">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">
              <Inbox className="w-3 h-3" /> Step 1
            </div>
            <div className="text-xs text-slate-700">
              We review your clinic profile and panel context.
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">
              <Workflow className="w-3 h-3" /> Step 2
            </div>
            <div className="text-xs text-slate-700">
              We map programs that fit your specialty, state, and size.
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">
              <Zap className="w-3 h-3" /> Step 3
            </div>
            <div className="text-xs text-slate-700">
              You receive a tailored fit briefing with next steps.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ClinicsLikeYours() {
  const [vocab, setVocab] = useState({ specialties: [], sizes: [], states: [] });
  const [submitted, setSubmitted] = useState(null);

  // SEO meta on mount.
  useEffect(() => {
    document.title = PAGE_TITLE;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", PAGE_DESC);
  }, []);

  // Vocab feeds the gated form selectors.
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/public/clinics-like-yours/vocab");
        setVocab(r.data);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
  }, []);

  // Scroll to the success state when the form submits.
  useEffect(() => {
    if (!submitted) return;
    const el = document.getElementById("cly-cta-anchor");
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [submitted]);

  const scrollToCta = () => {
    const el = document.getElementById("cly-cta-anchor");
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const heroPainCount = useMemo(() => PAIN_POINTS.length, []);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="clinics-like-yours-page">
      {/* Hero */}
      <section className="bg-gradient-to-b from-white to-slate-50 border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-14 lg:py-20">
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-slate-500">
            For independent clinics
          </div>
          <h1 className="mt-2 text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight">
            Clinics like yours <span className="text-slate-500">are using</span> Nodera Health.
          </h1>
          <p className="mt-4 text-base sm:text-lg text-slate-600 max-w-2xl">
            From functional medicine and longevity to weight optimization
            and hormone clinics — independent practices use Nodera to ship
            standardized patient programs, route multi-party payments, and
            run the operational backbone clinical teams shouldn&apos;t have
            to build themselves.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={scrollToCta}
              data-testid="cly-hero-cta"
              className="inline-flex items-center gap-2 rounded-full bg-slate-900 text-white text-sm font-bold px-5 py-2.5 hover:bg-slate-800"
            >
              <Lock className="w-4 h-4" />
              Access the fit briefing tool
            </button>
            <span className="text-xs text-slate-500">
              {heroPainCount} ways Nodera fits a clinic like yours · scroll to see
            </span>
          </div>
        </div>
      </section>

      {/* Pain points → answers */}
      <section className="max-w-6xl mx-auto px-6 py-14">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">
          What we hear from clinics like yours
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 max-w-3xl">
          The work clinical teams shouldn&apos;t have to invent from scratch.
        </h2>
        <p className="mt-3 text-sm text-slate-600 max-w-2xl">
          Every independent clinic running labs, programs, vendor partners,
          and recurring memberships hits the same operational walls. Here&apos;s
          how Nodera answers them — not with a generic platform pitch, but
          with the specific machinery clinics like yours already wish they had.
        </p>

        <div
          className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
          data-testid="cly-pain-points"
        >
          {PAIN_POINTS.map((pp, idx) => (
            <PainPointCard
              key={idx}
              icon={pp.icon}
              pain={pp.pain}
              answer={pp.answer}
              testid={`cly-pain-${idx}`}
            />
          ))}
        </div>
      </section>

      {/* Gated CTA */}
      <section
        id="cly-cta-anchor"
        className="bg-gradient-to-b from-slate-50 to-white border-y border-slate-200"
      >
        <div className="max-w-2xl mx-auto px-6 py-14 lg:py-20">
          {submitted ? (
            <ThankYouPanel result={submitted} />
          ) : (
            <GatedToolForm vocab={vocab} onSubmitted={(r) => setSubmitted(r)} />
          )}
        </div>
      </section>
    </div>
  );
}
